import { useState } from "react";
import { PocketRow, PocketSection } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import {
  FeedbackForm,
  type FeedbackType,
} from "@/components/settings/feedback-form";
import { useT } from "@/i18n";
import { settingsHelpMessages } from "@/i18n/messages/settings-help";
import type en from "@/i18n/messages/settings-help/en";
import { PRIVACY_URL, TERMS_URL } from "@/lib/legal-links";
import { openExternalUrl } from "@/lib/open-external-url";
import { useSettingsNav } from "./settings-navigation";

/** [test id, settings-help catalogue key, destination] */
type HelpLink = readonly [string, keyof typeof en, string];

const HELP_LINKS: readonly HelpLink[] = [
  ["changelog", "whatsNew", "https://www.denker.ai/docs/changelog"],
  [
    "support",
    "emailSupport",
    "mailto:support@denker.ai?subject=Denker%20Support",
  ],
];
const LEGAL_LINKS: readonly HelpLink[] = [
  ["privacy-policy", "privacyPolicy", PRIVACY_URL],
  ["terms-of-service", "terms", TERMS_URL],
];

function LinkRow({ link }: { link: HelpLink }) {
  const t = useT(settingsHelpMessages);
  return (
    <PocketRow
      testId={`help-link-${link[0]}`}
      title={t(link[1])}
      trailing={
        <Icons.ExternalLink className="h-3 w-3 text-muted" aria-hidden="true" />
      }
      onSelect={() => void openExternalUrl(link[2])}
    />
  );
}

export function HelpSection() {
  const t = useT(settingsHelpMessages);
  const nav = useSettingsNav();
  const requestedFeedback = nav.stack[nav.stack.length - 1]?.args?.feedback;
  const [feedbackType, setFeedbackType] = useState<
    FeedbackType | null | undefined
  >(() =>
    requestedFeedback === "ai_content"
      ? "ai_content"
      : requestedFeedback === "general"
        ? null
        : undefined,
  );
  if (feedbackType !== undefined) {
    return (
      <div data-testid="settings-help">
        <PocketSection>
          <FeedbackForm
            initialType={feedbackType ?? undefined}
            onClose={() => {
              setFeedbackType(undefined);
              if (requestedFeedback) nav.open({ page: "help" });
            }}
          />
        </PocketSection>
      </div>
    );
  }
  return (
    <div data-testid="settings-help">
      <PocketSection>
        {HELP_LINKS.map((link) => (
          <LinkRow key={link[0]} link={link} />
        ))}
        <PocketRow
          title={t("sendFeedback")}
          testId="help-link-feedback"
          chevron
          onSelect={() => setFeedbackType(null)}
        />
        <PocketRow
          title={t("reportAiContent")}
          testId="help-report-ai-content"
          chevron
          onSelect={() => setFeedbackType("ai_content")}
        />
      </PocketSection>
      <PocketSection>
        <PocketRow
          title={t("community")}
          testId="help-community"
          chevron
          onSelect={() => nav.push({ page: "help-community" })}
        />
      </PocketSection>
      <PocketSection>
        {LEGAL_LINKS.map((link) => (
          <LinkRow key={link[0]} link={link} />
        ))}
      </PocketSection>
    </div>
  );
}
